import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { Discount } from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';

interface PaginatedDiscountResponse {
  data: Discount[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export const useGetDiscount = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchDiscounts = async (
    page = 1,
    limit = 100,
    search = '',
    sortBy = 'name',
    order = 'ASC'
  ) => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<PaginatedDiscountResponse>(
        apiUrls.DISCOUNT_MANAGEMENT,
        {
          params: {
            offset: page,
            limit: limit,
            search: search,
            sortBy: sortBy,
            order: order,
          },
          withCredentials: true,
        }
      );

      setDiscounts(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setDiscounts([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deleteDiscount = async (id: string) => {
    const response = await axiosTokenInstance.delete(apiUrls.DELETE_DISCOUNT, {
      params: { id },
    });

    if (response.status === 200) {
      setDiscounts((prevDiscounts) =>
        prevDiscounts.filter((discount) => discount.id !== id)
      );

      if (discounts.length === 1) {
        setDiscounts([]);
      }
      showSnackbar(response.data.message, 'success');
    } else {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      fetchDiscounts();
    }
  };

  return {
    loading,
    discounts,
    totalItems,
    totalPages,
    currentPage,
    fetchDiscounts,
    deleteDiscount,
  };
};

export const getBasicDiscounts = async () => {
  const response = await axiosTokenInstance.get('/discount/list-basic');
  return response.data.data;
};
